
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { showToastError } from "@/helpers/notification";
import Call from "@/components/Call.vue";
import Spinner from "@/components/Spinner.vue";
import moment from "moment";
import {
  CallQueuePositionChangedReason,
  Config,
  EndpointStatus,
  IncidentDTO,
  IncidentType,
  MessageType,
  RemSignal,
  StatisticMode,
  TelephonyType,
  WebrtcPhoneState,
  ClientAppType,
  AuthorizationFailedReason,
  AcquireFailedRequestReason,
  RequestedAction,
  WebexAccessToken,
  ProfileDto
} from "@moderrotech/servers-communication-library/src/types";
import { i18n } from "@/lang";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import Topics from "@/components/Topics.vue";
import { useRoute, useRouter } from "vue-router";
import { setLocale } from "@vee-validate/i18n";
import TicketCreationRequest from "@/components/TicketCreationRequest.vue";
import TicketForm from "@/components/TicketForm.vue";
import TopPanel from "@/components/TopPanel.vue";
import { sendParentWindowMessage } from "@/helpers/embedded";
import { useIntervalFn, useLocalStorage, useCssVar } from "@vueuse/core";
import {
  clearNotifications,
  getDeviceInfo,
  getNotificationId,
  initApp,
  getRootUrl
} from "@moderrotech/servers-communication-library/src/helpers/cordova";
import { getMatchedLocale } from "@/validation/locales";
import { useWebSocket } from "@vueuse/core";
import { useTitle, useFavicon } from "@vueuse/core";
import axios from "axios";
import { useOnline } from "@vueuse/core";
import ErrorScreen from "@/components/ErrorScreen.vue";
import { preloadAvatarGlb } from "@/helpers/avatar";

export default {
  components: {
    ErrorScreen,
    TopPanel,
    TicketForm,
    TicketCreationRequest,
    Topics,
    Spinner,
    Call
  },
  setup() {
    const store = useStore();

    const router = useRouter(),
      route = useRoute();

    const isKioskType = computed(() => store.getters["auth/isKioskType"]),
      isKioskOs = computed(() => store.getters["auth/isKioskOs"]),
      isWebKiosk = computed(() => store.getters["auth/isWebKiosk"]);

    const config = computed((): Config => store.state.config.config);

    const profile = computed((): ProfileDto | null => store.state.auth.profile),
      appType = computed(() => profile.value?.app?.type);

    watch(
      () => isKioskType.value,
      v => {
        if (v) {
          _.each(
            _.pick(config.value, [
              "topicButtonBackgroundColor",
              "topicButtonBorderColor",
              "topicButtonTextColor",
              "topicButtonShadowColor",
              "topicButtonTextAlign",
              "topicMenuDirection",
              "appTextColor",
              "topicsPageTitleTextAlign",
              "sessionPageBrandingBackgroundColor",
              "sessionPageBrandingTextColor"
            ]),
            (v, k) => (useCssVar(`--${k}`).value = v)
          );

          const recalcSize = (px: number) =>
            `${(px / 1920) * 100}${
              appType.value === ClientAppType.kioskLandscape ? "vw" : "dvh"
            }`;

          _.each(
            _.pickBy(
              config.value,
              (v, k) =>
                _.includes(
                  [
                    "topicButtonBorderWidth",
                    "topicButtonHeight",
                    "topicButtonWidth",
                    "topicButtonRadius",
                    "topicButtonFontSize",
                    "topicMenuSpacing",
                    "topicsPageTitleWidth",
                    "topicsPageTitleHeight",
                    "topicsPageTitleFontSize",
                    "topicsPageLogoWidth",
                    "topicsPageLogoHeight",
                    "sessionPageBackgroundBlur"
                  ],
                  k
                ) && v !== null
            ),
            (v, k) => (useCssVar(`--${k}`).value = recalcSize(v))
          );

          _.each(["topicsPageTitleWidth", "topicsPageTitleHeight"], v => {
            if ((config.value?.[v] ?? null) === null) {
              useCssVar(`--${v}`).value = "auto";
            }
          });

          _.each(
            _.pick(config.value, [
              "topicMenuPosition",
              "topicsPageTitlePosition",
              "topicsPageTitlePosition",
              "topicsPageLogoPosition"
            ]),
            (v, k) =>
              _.each(
                ["top", "left", "bottom", "right"],
                p =>
                  (useCssVar(`--${k}${_.upperFirst(p)}`).value =
                    (v[p] ?? null) !== null ? recalcSize(v[p]) : "auto")
              )
          );
        }
      },
      { immediate: true }
    );

    const isReady = ref(false),
      isAppUnpublished = ref(false),
      authorizationFailedReason = ref<AuthorizationFailedReason | null>(null);

    const testEndpointStatusCode = ref();
    watch(testEndpointStatusCode, (v: EndpointStatus) =>
      store.commit("SET_ENDPOINT_STATUS", {
        details: _.startCase(
          _.find(
            _.entries(EndpointStatus).slice(
              _.entries(EndpointStatus).length / 2
            ),
            pair => pair[1] === v
          )?.[0]
        ),
        extension: "2012",
        parties: [],
        status: v
      })
    );

    const testQueuePosition = ref();
    watch(testQueuePosition, (position: number) =>
      store.commit("queue/SET_POSITION", position)
    );

    const testQueuePositionChangeReason = ref();
    watch(
      testQueuePositionChangeReason,
      (reason: CallQueuePositionChangedReason) =>
        store.commit("queue/SET_POSITION_CHANGE_REASON", reason)
    );

    const testAcquireFailedRequestReason = ref();
    watch(
      testAcquireFailedRequestReason,
      (reason: AcquireFailedRequestReason) =>
        store.commit("queue/SET_ACQUIRE_FAILED_REQUEST_REASON", reason)
    );

    const queuePosition = computed(() => store.state.queue.position),
      queuePositionChangeReason = computed(
        () => store.state.queue.positionChangeReason
      );

    const endpointStatusCode = computed(() => store.getters.endpointStatusCode),
      prevEndpointStatusCode = ref<EndpointStatus | null>(null);

    const isVisible = ref<boolean>(!document.hidden);

    watch(
      () => [
        endpointStatusCode.value === EndpointStatus.connected,
        isVisible.value
      ],
      ([v1, v2]) => {
        if (!v1 || typeof v2 !== "boolean") {
          return;
        }
        store.dispatch("session/sendJsonMessage", {
          message: {
            type: v2 ? MessageType.appShown : MessageType.appHidden
          }
        });
      },
      { immediate: true }
    );

    onMounted(() => {
      if (typeof document.addEventListener === "undefined") {
        return;
      }

      document.addEventListener(
        "visibilitychange",
        () => (isVisible.value = !document.hidden),
        false
      );
    });

    watch(
      () => endpointStatusCode.value,
      async (newVal, oldVal) => {
        if ((oldVal ?? null) !== null) {
          prevEndpointStatusCode.value = oldVal;
        }
      },
      { immediate: true }
    );

    const closeTabConfirmation = (e: BeforeUnloadEvent) => {
      e = e || window.event;

      const message = "Are you really sure to close/reload the page?";

      // For IE and Firefox prior to version 4
      if (e) {
        e.returnValue = message;
      }

      // For Safari
      return message;
    };

    const isCallScreenVisible = computed(
      () =>
        (route.name === "thank" &&
          [EndpointStatus.unknown, EndpointStatus.ready, null].includes(
            endpointStatusCode.value
          )) ||
        (queuePosition.value !== null && queuePosition.value >= 0) ||
        [
          EndpointStatus.transferring,
          EndpointStatus.placingCall,
          EndpointStatus.connected,
          EndpointStatus.failure
        ].includes(endpointStatusCode.value) ||
        (endpointStatusCode.value === EndpointStatus.ready &&
          prevEndpointStatusCode.value &&
          [EndpointStatus.connected, EndpointStatus.transferring].includes(
            prevEndpointStatusCode.value
          )) ||
        store.getters["queue/isThrownUrgently"] ||
        route.query.topic ||
        route.query.room_id
    );

    const defaultAppLocale = ref<string | null>(null),
      handleInactivityTimerTrigger = () => {
        if ((isKioskOs.value || isWebKiosk.value) && defaultAppLocale) {
          store.commit("config/PATCH_CONFIG", { appLocale: defaultAppLocale });
        }
      };

    const connectToSidekickApp = () => {
      const { status, send } = useWebSocket("ws://127.0.0.1:8080", {
        onConnected: () => console.log("Connected to Sidekick App"),
        onDisconnected: () => console.log("Disconnected from Sidekick App"),
        onMessage: (ws: WebSocket, event: MessageEvent) => {
          const data = JSON.parse(event.data);

          console.log("Sidekick App message received: ", data);
          store.dispatch("session/sendJsonMessage", {
            message: {
              type: MessageType.sidekickAppMessage,
              payload: data
            }
          });
        },
        autoReconnect: {
          retries: 3,
          delay: 1000,
          onFailed: () => console.error("Can not connect to Sidekick App")
        }
      });

      watch(
        () => [
          status.value === "OPEN",
          _.filter(store.state.session.requestedActions, {
            action: RequestedAction.sendSidekickAppMessage
          })
        ],
        ([v1, v2]) => {
          if (
            v1 &&
            (v2 as { action: RequestedAction; payload: object }[])?.length
          ) {
            _.forEach(
              (v2 ?? []) as { action: RequestedAction; payload: object }[],
              a => {
                const data = JSON.stringify(a.payload);
                console.log("Sending data to Sidekick App: ", data);
                send(data);
              }
            );
            store.commit(
              "session/UNSET_REQUESTED_ACTION",
              RequestedAction.sendSidekickAppMessage
            );
          }
        },
        { immediate: true }
      );
    };

    const removeClosingConfirmation = () =>
        window.removeEventListener("beforeunload", closeTabConfirmation),
      reloadPage = () => {
        console.log("Reloading page...");
        removeClosingConfirmation();
        window.location.reload();
      };

    const networkStatus = computed(() => store.state.network.status);

    watch(
      () => networkStatus.value,
      async v => {
        if (v === "shutdown" && route.name !== "shutdown") {
          removeClosingConfirmation();
          await router.replace({
            name: "shutdown"
          });
          window.location.reload();
        }
      }
    );

    onMounted(async () => {
      await router.isReady();

      if (route.name !== "shutdown") {
        connectToSidekickApp();
      }

      if (route.query.kiosk_id) {
        store.commit("auth/SET_KIOSK_ID", route.query.kiosk_id);
        await router.replace({
          name: route.name!,
          params: route.params,
          query: _.omit(route.query, "kiosk_id")
        });
      }

      const kioskId = store.state.auth.kioskId;

      if (kioskId) {
        store.commit("metadata/UNSET_CUSTOMER_INFO");
      }

      let config;

      const timerInterval = 1 * 60 * 1000; // 1 minute

      const { pause: pauseConfigRetrievingTimer } = useIntervalFn(
        async () => {
          try {
            config = await store.dispatch("config/getConfig", {
              isTemplatesSupported: true
            });

            defaultAppLocale.value =
              store.state.config.config.appLocale ?? null;

            pauseConfigRetrievingTimer();
            isAppUnpublished.value = false;
          } catch (err) {
            isAppUnpublished.value = true;

            if (!kioskId) {
              pauseConfigRetrievingTimer(); // retry only if app launched on kiosk
            }

            return;
          }

          const supportedLocales = _.keys(
              _.pickBy(
                store.state.config.config.appLocalesOrder ?? {},
                l => l >= 0
              )
            ),
            storedLocale = useLocalStorage("app.locale", null);

          if (!_.includes(supportedLocales, storedLocale.value)) {
            storedLocale.value = null;
          }

          const browserLocale = _.first(_.split(navigator.language, "-")),
            locale =
              storedLocale.value ??
              (!kioskId &&
              browserLocale &&
              _.includes(supportedLocales, browserLocale)
                ? browserLocale
                : store.state.config.config.appLocale);

          const cameraLabel = (route.query.camera_label ?? null) as
            | string
            | null;

          if (cameraLabel) {
            // used for app opened on kiosk

            store.commit("session/mediaDevices/SET_FORCED_CAMERA", cameraLabel);

            await router.replace({
              name: route.name!,
              params: route.params,
              query: _.omit(route.query, "camera_label")
            });
          }

          const extraConfig = _.assign(
            _.omit(
              {
                publicPath: process.env.VUE_APP_PUBLIC_PATH
                  ? `/${_.trim(process.env.VUE_APP_PUBLIC_PATH, "/")}/`
                  : "/",
                clientType: "kiosk"
              },
              _.keys(config)
            ),
            locale !== store.state.config.config.appLocale
              ? { appLocale: locale }
              : {}
          );

          store.commit("config/PATCH_CONFIG", extraConfig);

          let indexHtml: string;

          useIntervalFn(
            async () => {
              console.log("Retrieving index.html file content...");

              const html = (
                await axios.get(store.state.config.config.publicPath, {
                  params: { timestamp: moment().unix() }
                })
              ).data;

              console.log("Retrieved index.html file content: ", {
                html: _.truncate(html, {
                  length: 100
                })
              });

              if (!indexHtml) {
                indexHtml = html;
              } else if (indexHtml !== html) {
                console.log(
                  "index.html file content was changed. App new build was uploaded"
                );
                reloadPage();
              }
            },
            60 * 1000,
            {
              immediateCallback: true
            }
          );

          if (store.state.config.config?.webPageCustomTitle) {
            useTitle(store.state.config.config?.webPageCustomTitle as string);
          }
          if (store.state.config.config?.customFavicon) {
            useFavicon(store.state.config.config?.customFavicon as string);
          }

          const deviceId =
            kioskId ?? store.state.auth.deviceId ?? `{${uuidv4()}}`;
          store.commit("auth/SET_DEVICE_ID", deviceId);

          if (route.name === "shutdown") {
            store.commit("network/SET_STATUS", "shutdown");
            isReady.value = true;
            return;
          }

          store.commit("auth/UNSET_JWT");

          const { pause: pauseLoginTimer } = useIntervalFn(
            async () => {
              try {
                await store.dispatch("auth/login", {
                  id: profile.value?.id
                });
                pauseLoginTimer();
                authorizationFailedReason.value = null;
              } catch (err) {
                authorizationFailedReason.value = _.get(
                  err,
                  "response.data.data.reason",
                  AuthorizationFailedReason.noPtpcResponse
                ) as AuthorizationFailedReason;

                if (!kioskId) {
                  pauseLoginTimer(); // retry only if app launched on kiosk
                }

                return;
              }

              if (isKioskOs.value) {
                store.dispatch("arecorder/init", {
                  callback: () => {
                    /* empty */
                  },
                  errorCallback: (): void =>
                    showToastError("Can not init arecorder")
                });
              } else {
                const cordovaRoot = useLocalStorage(
                  "cordova.root",
                  route.query.cordova_root ?? ""
                );

                if (cordovaRoot.value) {
                  const rootUrl = getRootUrl(
                    cordovaRoot.value as string,
                    `${window.location.origin}${store.state.config.config
                      ?.publicPath ?? ""}`
                  );

                  await router.replace({
                    name: route.name!,
                    params: route.params,
                    query: _.omit(route.query, "cordova_root")
                  });

                  try {
                    await initApp({
                      rootUrl,
                      screenOrientation:
                        appType.value === ClientAppType.kioskLandscape
                          ? "landscape"
                          : "portrait",
                      callbacks: {
                        answer: () => store.dispatch("session/answer"),
                        hangup: () => store.dispatch("session/hangup")
                      }
                    });
                    store.commit("config/PATCH_CONFIG", {
                      mobileAppData: {
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        notification_id: getNotificationId(),
                        platform: getDeviceInfo()?.platform
                      }
                    });
                    clearNotifications();
                  } catch (err) {
                    console.error("Can not init Cordova mobile app: ", err);
                  }
                }
              }

              watch(
                () => store.state.config.config.appLocale,
                (newVal, oldVal) => {
                  if (newVal) {
                    i18n.global.locale = newVal;
                    setLocale(getMatchedLocale(newVal));

                    moment.locale(_.toLower(_.replace(newVal, "_", "-")));

                    if (oldVal) {
                      // changed manually
                      storedLocale.value = newVal;
                    }
                  }
                },
                { immediate: true }
              );

              _.forEach(["male", "female"], g =>
                preloadAvatarGlb(
                  `${store.state.config.config.publicPath}avatars/${g}-merged.glb`
                )
              );

              store.dispatch("network/setCallbacks", {
                onTimedOut: () => {
                  console.log(
                    "Network connection timed out, reloading web page..."
                  );
                  reloadPage();
                }
              });

              store.dispatch("incident/get", {
                callback: () => {
                  store.dispatch("getTelephonyType", {
                    callback: (telType: TelephonyType) => {
                      if (telType === TelephonyType.webex) {
                        const cb = () =>
                          store.dispatch("webex/init", {
                            createMeeting: true
                          });

                        if (
                          !store.state.webex.accessToken ||
                          store.state.webex.accessToken?.expires <=
                            moment().unix()
                        ) {
                          store.dispatch("webex/getAccessToken", {
                            callback: (token: WebexAccessToken) => cb(),
                            errorCallback: (): void =>
                              showToastError("Can not get Webex access token")
                          });
                        } else {
                          cb();
                        }
                      }
                      store.dispatch("signals/subscribe", {
                        callback: () => {
                          isReady.value = true;
                          store.dispatch("topics/getList", {
                            callback: async () => {
                              if (telType === TelephonyType.twilio) {
                                store.dispatch("setEndpointStatus", {
                                  status: EndpointStatus.ready
                                });
                              }
                              store.dispatch("getEndpointStatus");
                              store.dispatch("queue/getPosition", {
                                callback: () => store.dispatch("getHints")
                              });
                              if (telType === TelephonyType.webrtc) {
                                store.dispatch("webrtc/getPhoneState");
                              } else if (telType === TelephonyType.zoom) {
                                await store.dispatch("zoom/init");
                              }
                            }
                          });
                        }
                      });
                    }
                  });
                }
              });
            },
            timerInterval,
            {
              immediateCallback: true
            }
          );
        },
        timerInterval,
        {
          immediateCallback: true
        }
      );
    });

    watch(
      () => isCallScreenVisible.value,
      v => {
        if (v) {
          window.addEventListener("beforeunload", closeTabConfirmation);
        } else {
          window.removeEventListener("beforeunload", closeTabConfirmation);
        }
      }
    );

    watch(
      () =>
        route.name === "thank" &&
        ![EndpointStatus.unknown, EndpointStatus.ready, null].includes(
          endpointStatusCode.value
        ),
      v => {
        if (v) {
          router.replace({
            name: "app",
            params: route.params,
            query: _.pick(route.query, ["login_type"])
          });
        }
      }
    );

    const telephonyType = computed(() => store.state.telephonyType);

    const feedbackPayload = ref<{
      session?: {
        id?: string;
        started_at?: string;
      };
      // expert?: {
      //   extension: string;
      // };
      // client?: {
      //   extension: string;
      // };
    }>({});

    const toTopics = async () => {
      prevEndpointStatusCode.value = null;
      await router.replace({
        name: "app",
        params: route.params,
        query: _.pick(route.query, ["login_type"])
      });
      feedbackPayload.value = {};
      _.each(
        ["UNSET_POSITION_CHANGE_REASON", "UNSET_ACQUIRE_FAILED_REQUEST_REASON"],
        v => store.commit(`queue/${v}`)
      );
      if (endpointStatusCode.value === EndpointStatus.failure) {
        reloadPage();
      }
    };

    watch(
      () =>
        store.state.queue.positionChangeReason ===
          CallQueuePositionChangedReason.released &&
        endpointStatusCode.value !== EndpointStatus.failure,
      v => {
        if (v) {
          toTopics();
        }
      },
      { immediate: true }
    );

    const getProperty = (name: string) => store.getters["auth/property"](name);

    const getRedirectionUrl = (): string | null =>
        route.query.redirect ?? getProperty("after.call.redirect.url") ?? null,
      redirect = () => {
        const url = getRedirectionUrl();

        if (!url) {
          return;
        }

        const cb = () => {
          window.removeEventListener("beforeunload", closeTabConfirmation);

          if (telephonyType.value === TelephonyType.webex) {
            store.dispatch("webex/leaveMeeting", {
              callback: () => (window.location.href = url),
              errorCallback: () => (window.location.href = url)
            });
          } else {
            window.location.href = url;
          }
        };

        store.dispatch("queue/release", {
          callback: cb,
          errorCallback: cb
        });
      };

    const hangupRequestSentAt = ref<number | null>(null),
      handleHangupRequestSent = () => {
        if (getRedirectionUrl()) {
          redirect();
          return;
        }
        hangupRequestSentAt.value = +moment();
      };

    watch(
      () =>
        !!getRedirectionUrl() &&
        store.state.session.lastRequest?.signal === RemSignal.hangupRequested,
      val => {
        if (val) {
          redirect();
        }
      }
    );

    const isTelephonySubordinate = computed(
      () => store.getters.isTelephonySubordinate
    );

    watch(
      () =>
        !hangupRequestSentAt.value &&
        prevEndpointStatusCode.value === EndpointStatus.placingCall &&
        endpointStatusCode.value === EndpointStatus.ready &&
        !isTelephonySubordinate.value, // not for mustang
      val => {
        if (val) {
          // expert did not answer - show 'no experts' error page
          store.dispatch("queue/getPosition", {
            callback: () => {
              if (queuePosition.value < 0) {
                store.commit(
                  "queue/SET_POSITION_CHANGE_REASON",
                  CallQueuePositionChangedReason.noExperts
                );
              }
            }
          });
        }
      }
    );

    watch(
      () =>
        !hangupRequestSentAt.value &&
        prevEndpointStatusCode.value === EndpointStatus.transferring &&
        endpointStatusCode.value === EndpointStatus.ready,
      val => {
        if (val) {
          // call was transferred, but expert did not answer - show 'no experts' error page
          store.dispatch("queue/getPosition", {
            callback: () => {
              if (queuePosition.value < 0) {
                store.commit(
                  "queue/SET_POSITION_CHANGE_REASON",
                  CallQueuePositionChangedReason.noExperts
                );
              }
            }
          });
        }
      }
    );

    watch(
      () =>
        !!hangupRequestSentAt.value &&
        prevEndpointStatusCode.value === EndpointStatus.placingCall &&
        endpointStatusCode.value === EndpointStatus.ready,
      val => {
        if (val) {
          if (getRedirectionUrl()) {
            redirect();
            return;
          }

          // client clicked 'cancel call' button before expert's answer - show topics page
          hangupRequestSentAt.value = null;
          toTopics();
        }
      }
    );

    watch(
      () => [!!hangupRequestSentAt.value, queuePosition.value],
      (
        [newIsHangupRequestSent, newQueuePosition],
        [oldIsHangupRequestSent, oldQueuePosition]
      ) => {
        if (
          [EndpointStatus.ready, EndpointStatus.unknown, null].includes(
            endpointStatusCode.value
          ) &&
          newIsHangupRequestSent &&
          oldIsHangupRequestSent &&
          newQueuePosition < 0 &&
          oldQueuePosition >= 0
        ) {
          if (getRedirectionUrl()) {
            redirect();
            return;
          }

          // client clicked 'call' button before telephony initialization and 'cancel call' one before expert's answer - show topics page
          hangupRequestSentAt.value = null;
          toTopics();
        }
      }
    );

    const sessionInfo = computed(() => store.state.session.info);

    watch(
      () => endpointStatusCode.value === EndpointStatus.connected,
      async val => {
        if (val) {
          feedbackPayload.value.session = _.assign(
            {},
            feedbackPayload.value.session ?? {},
            {
              // eslint-disable-next-line @typescript-eslint/camelcase
              started_at: moment()
                .utc()
                .format()
            }
          );
        }
      },
      { immediate: true }
    );

    watch(
      () => sessionInfo.value,
      v => {
        if (v?.id) {
          feedbackPayload.value.session = _.assign(
            {},
            feedbackPayload.value.session ?? {},
            { id: v.id as string }
          );
        }
        // if (v?.companion_endpoint_status?.extension) {
        //   feedbackPayload.expert = {
        //     extension: v.companion_endpoint_status.extension as string
        //   };
        // }
      },
      { immediate: true }
    );

    const isCallScreenContentHidden = ref(false);

    const redirectToThankPage = async () => {
      const isWebex = telephonyType.value === TelephonyType.webex;

      if (isWebex) {
        isCallScreenContentHidden.value = true;
      }

      await router.replace({
        name: "thank",
        params: route.params,
        query: _.assign(
          {
            // eslint-disable-next-line @typescript-eslint/camelcase
            feedback_payload: JSON.stringify(feedbackPayload.value)
          },
          _.pick(route.query, ["login_type"])
        )
      });

      if (isWebex) {
        reloadPage();
      }
    };

    watch(
      () =>
        prevEndpointStatusCode.value === EndpointStatus.connected &&
        endpointStatusCode.value === EndpointStatus.ready &&
        route.name !== "thank",
      async val => {
        if (val) {
          isCallScreenContentHidden.value = true;

          store.dispatch("queue/getPosition", {
            callback: async () => {
              isCallScreenContentHidden.value = false;

              if (queuePosition.value >= 0) {
                // transfer to another topic in progress
                return;
              }

              if (getRedirectionUrl()) {
                redirect();
                return;
              }

              // call ended properly (after establishing)

              // notify parent window about call finishing (if app opened in iframe)
              sendParentWindowMessage("call-ended");

              await redirectToThankPage();
            }
          });
        }
      }
    );

    watch(
      () =>
        prevEndpointStatusCode.value === EndpointStatus.transferring &&
        endpointStatusCode.value === EndpointStatus.ready &&
        route.name !== "thank",
      async val => {
        if (val) {
          if (hangupRequestSentAt.value) {
            // client cancelled call during a transfer
            await redirectToThankPage();
            return;
          }
          store.dispatch("queue/getPosition", {
            callback: () => {
              if (queuePosition.value < 0) {
                // expert did not answer - show 'no experts' error page
                store.commit(
                  "queue/SET_POSITION_CHANGE_REASON",
                  CallQueuePositionChangedReason.noExperts
                );
              } else {
                redirectToThankPage();
              }
            }
          });
        }
      }
    );

    const jwt = computed(() => store.state.auth.jwt);

    const isConsoleListening = computed(
      () => store.state.auth.isConsoleListening
    );

    const {
      pause: pauseLogStoreTimer,
      resume: resumeLogStoreTimer
    } = useIntervalFn(
      async () => {
        store.dispatch("log/store");
      },
      5 * 60 * 1000,
      {
        immediateCallback: false
      }
    );

    watch(
      () => !!jwt.value,
      async (newVal, oldVal) => {
        if (newVal && !oldVal && profile.value?.store_logs) {
          if (!isConsoleListening.value) {
            store.dispatch("log/listenConsole");
          }
          resumeLogStoreTimer();
        } else if (!newVal && oldVal) {
          if (isConsoleListening.value) {
            store.dispatch("log/stopConsoleListening");
          }
          pauseLogStoreTimer();
          store.commit("log/UNSET_LIST");
        }
      },
      { immediate: true }
    );

    let statisticTimer: number;
    watch(
      () => endpointStatusCode.value === EndpointStatus.connected,
      v => {
        if (v) {
          if (telephonyType.value !== TelephonyType.webex) {
            return;
          }

          const mode =
            getProperty("webex.statistics.mode") ?? StatisticMode.disabled;

          if (mode === StatisticMode.disabled) {
            return;
          }

          statisticTimer = window.setInterval(
            () => store.dispatch("session/setStatistic"),
            getProperty("webex.statistics.timeout") as number
          );
        } else {
          window.clearInterval(statisticTimer);
        }
      }
    );

    const appVersion = computed(() => process.env.VUE_APP_VERSION);

    onMounted(async () => {
      if (appVersion.value) {
        console.log("App version: ", appVersion.value);
      }
    });

    const isReconnectingScreenVisible = computed(
      () =>
        endpointStatusCode.value === EndpointStatus.connected &&
        (networkStatus.value === "disconnected" ||
          (telephonyType.value === TelephonyType.webrtc &&
            store.state.webrtc.phoneState?.state === WebrtcPhoneState.onCall &&
            store.state.webrtc.networkStatus === "disconnected"))
    );

    return {
      toTopics,
      handleInactivityTimerTrigger,
      handleHangupRequestSent,
      config,
      isReady,
      endpointStatusCode,
      prevEndpointStatusCode,
      testEndpointStatusCode,
      testQueuePosition,
      testQueuePositionChangeReason,
      testAcquireFailedRequestReason,
      queuePosition,
      queuePositionChangeReason,
      isCallScreenVisible,
      networkStatus,
      redirect,
      jwt,
      isCallScreenContentHidden,
      route,
      getProperty,
      isAppUnpublished,
      authorizationFailedReason,
      AuthorizationFailedReason,
      isReconnectingScreenVisible,
      isKioskType,
      EndpointStatus,
      CallQueuePositionChangedReason,
      incident: computed(
        (): IncidentDTO | null => store.state.incident.incident
      ),
      IncidentType,
      appType,
      appVersion,
      ClientAppType,
      AcquireFailedRequestReason,
      appName: process.env.VUE_APP_NAME,
      isWebKiosk,
      isOnline: useOnline(),
      ..._.pick(_, ["entries"])
    };
  }
};
